import { CareEnvironment, CarePhase } from '../../../../appModel';
import { appliedHealthCareInflation } from '../../../../appliedHealthCareInflation';
import {
  BuildYearSnapshotInput,
  FinalizeDeathBenefitInput,
  MonthComputeInput,
  PolicyProjectionStrategy,
} from '../strategyTypes';
import { DAYS_IN_MONTH, unlimitedMaxBenefitMagicNumber } from '../sharedConstants';
import { computeMonthlyCostMetrics, computeNon2AdlDaysThisRow } from '../sharedEligibility';
import {
  consumeWaitingPeriodDaysForEnvironment,
  growthFactor,
  isIndependentLivingBenefitZeroedOut,
  getCalendarPolicyYear,
  getCareCostAnchorYear,
  getDailyBenefitAmountForEnvironment,
  getFirstContributingPhaseStartYear,
  getMaxDailyBenefitAmount,
  getPhaseCareMonthsByPhase,
  resolveRowPolicyYear,
} from '../sharedPolicyConfig';
import { calculateClientAge } from '../clientAgeCalculation';
import { deriveStandardMilestones } from '../deriveMilestones';
import {
  deriveIneligibilityReason,
  deriveIneligibleDayBreakdown,
} from '../deriveIneligibilityReason';
import { buildPrePolicyMonth } from '../buildPrePolicyMonth';
import { applyIndemnitySurplus } from '../sharedSurplus';
import { Claimant, JointWaitingPeriod, seedJointWaitingPeriod } from '../jointClaimant';

// ---------------------------------------------------------------------------
// Hybrid Life IRA — OneAmerica Asset Care III / Indexed Annuity Care
// (calculationType `hybridLifeIra`).
//
// >>> UNVERIFIED MODELING — these policy types are flagged
// >>> `hasUnverifiedModeling: true` in FundingPolicyTypeDef.ts. <<<
//
// There is NO carrier illustration in this repo for Asset Care III or Indexed
// Annuity Care. The mechanics below mirror the legacy aggregate calc
// (`calcFundingSourceDerivatives.ts`, the `isCalculationTypeHybridLifeIra`
// branches), with one deliberate deviation where legacy was an artifact rather
// than product behavior (see "COB rider daily" below).
//
// Structurally this is an IRA-funded Asset Care variant, so `oaAssetCareStrategy`
// is the primary reference. Two pools:
//   - base pool   = Face / Acceleration of Benefits (`policyMaximumBenefitAmount`)
//   - eob pool    = Continuation of Benefits (`policyContinuationBenefitAmount`)
// Base drawn first, then COB; the flat Face Amount death benefit is depleted
// dollar-for-dollar by base-pool drawdown only. `isHybridPolicy(hybridLifeIra)`
// is true (`calcFundingSourceDerivatives.ts:2470-2475`), routing the policy
// through the two-pool COB+DB path (`calculateHybridPolicyBenefit`).
//
// THE IRA-SPECIFIC DIVERGENCE — withdrawal-rate base payout:
// Unlike Asset Care (which uses a fixed per-environment daily benefit), the
// base-pool daily rate here is derived from a monthly withdrawal rate applied
// to the inflated FULL face amount (legacy `:770-777`):
//
//     baseDailyCap = (inflatedFace × policyMonthlyWithdrawalRate) / 30
//     basePerDay   = isIndemnity ? baseDailyCap : min(dailyCost, baseDailyCap)
//
// The rate is computed off the inflated full face (NOT the remaining pool); the
// day-count is bounded by the running pool balance — matching legacy
// (`:780-783`).
//
// Missing-vs-explicit-0 daily benefit (legacy `:618` and `:641`):
//   - An EXPLICIT 0 per-environment daily benefit hard-zeroes the phase (the
//     `:618` early return is NOT excluded for hybridLifeIra). Mirrored here: an
//     explicit 0 daily for the active environment ⇒ no payout this row.
//   - A MISSING (null/undefined) daily benefit is explicitly EXCLUDED from the
//     short-circuit at `:641` (`&& !isCalculationTypeHybridLifeIra`), so the
//     withdrawal-rate base path still fires. These products quote a monthly
//     withdrawal rate, not a daily max, so a missing daily is the expected case.
//
// COB rider daily source (DELIBERATE DEVIATION from legacy):
// Legacy sizes the COB/rider daily payout from the inflated per-environment
// *daily benefit amount* (`:788-790`), inflated on the COB rate (or policy-wide
// fallback). hybridLifeIra products typically carry NO per-environment daily
// benefit, in which case the legacy rider daily was 0 and the COB pool paid
// nothing — an artifact, not product behavior: the COB rider continues the
// policy's monthly benefit once the base pool exhausts. So when NO
// per-environment daily is configured anywhere on the policy, the COB leg
// falls back to the withdrawal-rate daily (same rate as the base leg).
// Policies that do quote a daily keep the legacy inflated-daily rider math.
//
// Unlimited COB (legacy-parity RESTORED): hybrid calc types treat a missing
// `policyContinuationBenefitAmount` as UNLIMITED, not $0 — legacy
// `calcPolicyTotalLtcLimitForPhase` short-circuits through
// `handleUnlimitedBenefit` to `unlimitedMaxBenefitMagicNumber` and flags
// `policyBenefitIsUnlimited`. The COB pool seeds the sentinel and stays flat
// (no inflation accrual, no prior-utilization reduction), mirroring
// traditionalLtcStrategy's unlimited handling. `buildClaimTerms` sets
// `terms.isUnlimited` so total-pool UI surfaces render "Unlimited"; per-pool
// surfaces compare each pool to the sentinel since the base pool stays
// finite.
//
// Indemnity (verified from legacy, NOT the Asset Care model):
// Legacy hybridLifeIra indemnity is uncapped daily-rate × eligible days and
// NEVER reads `policyIndemnityBenefitPercent` (`:775-777`, `:791-793`). We
// introduce NO indemnity-percent multiplier and do NOT copy Asset Care's
// AOB-only / indemnity-percent cash model.
//
// Like Asset Care, no per-claim/per-month carry-forward beyond the universal
// pools/waitingPeriod/payout shape — both state and deltas slots are empty.
// The optional jointWaitingPeriod slot is kept for joint-readiness (mirroring
// Asset Care); the joint variants (jointHybridLifeIra / jointAssetcareThree)
// are NOT wired in pickStrategy yet and fall through to legacy.
// ---------------------------------------------------------------------------

export interface HybridLifeIraState {
  // Indemnity-surplus reserve. Indemnity policies pay the uncapped daily rate
  // as cash regardless of cost; the excess above eligible cost is banked here
  // and drained on later shortfall months (legacy `applyIndemnityPayoutToPhase`
  // parity). Always 0 on reimbursement-election policies (their payout never
  // exceeds eligible cost), so the surplus model is a numeric no-op there.
  indemnitySurplus: number;
  jointWaitingPeriod?: JointWaitingPeriod;
}
export interface HybridLifeIraDeltas {
  indemnitySurplusAccrued: number;
  indemnitySurplusApplied: number;
}

interface InflationConfig {
  baseRate: number;
  eobRate: number;
  capYears: number;
  isSimple: boolean;
}

// Each pool reads its own inflation rate, falling back to the policy-wide rate
// when the pool-specific knob is missing — matching the legacy fallback
// (`policyInflationProtectionOnFaceAmount ?? policyInflationProtection`,
// `policyInflationProtectionOnCOBAmount ?? policyInflationProtection`).
function readInflation(ltcPolicy: PolicyFundingSource): InflationConfig {
  const policyWide = ltcPolicy.policyInflationProtection ?? 0;
  return {
    baseRate: ltcPolicy.policyInflationProtectionOnFaceAmount ?? policyWide,
    eobRate: ltcPolicy.policyInflationProtectionOnCOBAmount ?? policyWide,
    capYears: ltcPolicy.policyInflationProtectionYears ?? 0,
    // Simple riders grow by a fixed step per anniversary (legacy
    // `calcInflationProtectionFutureValue` branches on this flag; the
    // strategy must too — P2-38).
    isSimple: ltcPolicy.isSimpleInflationProtection === true,
  };
}

// Hybrid calc types treat a missing COB amount as UNLIMITED, not $0 — legacy
// `calcPolicyTotalLtcLimitForPhase` short-circuits to the sentinel via
// `handleUnlimitedBenefit`. A populated amount (including 0) is a real cap.
function isUnlimitedCob(ltcPolicy: PolicyFundingSource): boolean {
  return ltcPolicy.policyContinuationBenefitAmount == null;
}

function readAtIssuePools(ltcPolicy: PolicyFundingSource): {
  baseAtIssue: number;
  eobAtIssue: number;
} {
  return {
    baseAtIssue: ltcPolicy.policyMaximumBenefitAmount ?? 0,
    eobAtIssue: isUnlimitedCob(ltcPolicy)
      ? unlimitedMaxBenefitMagicNumber
      : (ltcPolicy.policyContinuationBenefitAmount ?? 0),
  };
}

// Inflated COB pool for a policy year. Unlimited pools stay flat at the
// sentinel — inflating a magic number is meaningless and would break the
// client's `>= sentinel` "Unlimited" rendering convention.
function inflatedEobPoolForYear(ltcPolicy: PolicyFundingSource, policyYear: number): number {
  const { eobAtIssue } = readAtIssuePools(ltcPolicy);
  if (isUnlimitedCob(ltcPolicy)) return eobAtIssue;
  const { eobRate, capYears, isSimple } = readInflation(ltcPolicy);
  return eobAtIssue * growthFactor(eobRate, capYears, isSimple, policyYear);
}

// Prior utilization draws the base (Acceleration) pool down first; any excess
// beyond the base pool spills into the COB pool instead of being clamped away
// (mirrors indexedAnnuityCareStrategy's applyPriorUtilization).
function splitPriorUtilization(
  utilizedToDate: number,
  baseAtClaimStart: number,
  eobAtClaimStart: number,
  eobIsUnlimited: boolean,
): { base: number; eob: number } {
  const base = Math.min(Math.max(utilizedToDate, 0), baseAtClaimStart);
  // An unlimited COB pool is not dented by prior utilization — legacy returns
  // the sentinel before utilized-to-date enters the calc.
  const eob = eobIsUnlimited
    ? 0
    : Math.min(Math.max(utilizedToDate - base, 0), eobAtClaimStart);
  return { base, eob };
}

// The base-pool daily rate is the inflated FULL face × monthly withdrawal rate
// / 30 (legacy `:770-774`). Computed off the inflated at-issue face for the
// year — NOT the remaining pool balance — so the rate is path-independent and
// matches legacy. The pool balance separately bounds how many days the base
// can fund.
// `careEnvironment` is optional: per-month callers pass the active environment
// so the shop-insurance Independent Living zero-out silences the base leg the
// same way it silences the COB leg (P2-39 — legacy zeroes the daily once,
// upstream of both legs); environment-agnostic callers (terms/snapshots) omit
// it.
function baseDailyCapForYear(
  ltcPolicy: PolicyFundingSource,
  policyYear: number,
  careEnvironment?: CareEnvironment,
): number {
  if (
    careEnvironment === CareEnvironment.independentLiving &&
    isIndependentLivingBenefitZeroedOut(ltcPolicy)
  ) {
    return 0;
  }
  const { baseAtIssue } = readAtIssuePools(ltcPolicy);
  const { baseRate, capYears, isSimple } = readInflation(ltcPolicy);
  const inflatedFace = baseAtIssue * growthFactor(baseRate, capYears, isSimple, policyYear);
  const withdrawalRate = ltcPolicy.policyMonthlyWithdrawalRate ?? 0;
  return (inflatedFace * withdrawalRate) / DAYS_IN_MONTH;
}

function rawDailyForEnvironment(
  ltcPolicy: PolicyFundingSource,
  careEnvironment: CareEnvironment,
): number | null | undefined {
  switch (careEnvironment) {
    case CareEnvironment.home:
      return ltcPolicy.dailyHomeBenefitAmount;
    case CareEnvironment.assistedLiving:
      return ltcPolicy.dailyAssistedLivingBenefitAmount;
    case CareEnvironment.fullCareFacility:
      return ltcPolicy.dailyNursingHomeBenefitAmount;
    case CareEnvironment.independentLiving:
    default:
      return ltcPolicy.dailyIndependentLivingBenefitAmount;
  }
}

// A daily benefit counts as quoted only when some environment carries a
// POSITIVE daily. Explicit 0s alone do NOT count — these products quote a
// monthly withdrawal rate, and an all-zero/missing daily set means "no daily
// quoted", not "$0 COB". (Guards against the planned persistence of
// `dailyIndependentLivingBenefitAmount = 0` on shop-insurance policies —
// sharedPolicyConfig.ts TODO — silently disabling the fallback below.)
function hasQuotedDailyBenefit(ltcPolicy: PolicyFundingSource): boolean {
  return getMaxDailyBenefitAmount(ltcPolicy) > 0;
}

// COB/rider daily rate. Policies that quote a per-environment daily keep the
// legacy rider math (`:788-790`): the inflated daily, grown on the COB rate
// (policy-wide fallback). Policies that quote NO daily at all — the normal
// shape for these withdrawal-rate products — continue the withdrawal-rate
// daily on the COB leg instead of the legacy $0 (deliberate deviation; see
// header). The fallback rides `baseDailyCapForYear`, so it grows on the FACE
// rate (it IS the base benefit continuing) and inherits the shop-insurance
// Independent Living zero-out.
function cobDailyCapForYear(
  ltcPolicy: PolicyFundingSource,
  careEnvironment: CareEnvironment,
  policyYear: number,
): number {
  if (!hasQuotedDailyBenefit(ltcPolicy)) {
    // An explicit 0 for this environment is authoritative ("not covered
    // here") even when it is the only populated daily field.
    if (rawDailyForEnvironment(ltcPolicy, careEnvironment) === 0) return 0;
    return baseDailyCapForYear(ltcPolicy, policyYear, careEnvironment);
  }
  const { eobRate, capYears, isSimple } = readInflation(ltcPolicy);
  // getDailyBenefitAmountForEnvironment honors an explicit 0 and falls back to
  // the max populated daily for sparse imports.
  const atIssueDaily = getDailyBenefitAmountForEnvironment(ltcPolicy, careEnvironment);
  return atIssueDaily * growthFactor(eobRate, capYears, isSimple, policyYear);
}

export const hybridLifeIraStrategy: PolicyProjectionStrategy<
  HybridLifeIraState,
  HybridLifeIraDeltas
> = {
  initClaimState(client, ltcPolicy, jointAnchorPolicyYear): MonthlyState<HybridLifeIraState> {
    const { baseAtIssue } = readAtIssuePools(ltcPolicy);
    const faceAmount = ltcPolicy.policyMaximumBenefitAmount ?? 0;
    const utilizedToDate = ltcPolicy.policyBenefitUtilizedToDate ?? 0;
    const minGuaranteed = ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;
    const { baseRate, capYears, isSimple } = readInflation(ltcPolicy);
    // Anniversary inflation compounds every policy year regardless of whether a
    // claim has started; pre-inflate at claim start so opening pools match the
    // carrier's current pool (constraints 6/7). Claims starting at policy year 1
    // keep both factors at 1.0. An unlimited COB pool stays flat at the
    // sentinel.
    const claimStartPolicyYear =
      jointAnchorPolicyYear ?? getCalendarPolicyYear(client as Client, 0);
    const baseAtClaimStart =
      baseAtIssue * growthFactor(baseRate, capYears, isSimple, claimStartPolicyYear);
    const eobAtClaimStart = inflatedEobPoolForYear(ltcPolicy, claimStartPolicyYear);
    const waitingPeriod: WaitingPeriodState = {
      homeDays:
        ltcPolicy.homeCareWaitingPeriodDaysRemaining ?? ltcPolicy.homeCareWaitingPeriodDays ?? 0,
      facilityDays:
        ltcPolicy.facilityCareWaitingPeriodDaysRemaining ??
        ltcPolicy.facilityCareWaitingPeriodDays ??
        0,
    };
    const isJoint = jointAnchorPolicyYear != null;
    const prior = splitPriorUtilization(
      utilizedToDate,
      baseAtClaimStart,
      eobAtClaimStart,
      isUnlimitedCob(ltcPolicy),
    );
    return {
      pools: {
        baseBenefitPool: Math.max(baseAtClaimStart - prior.base, 0),
        extensionOfBenefitsPool: Math.max(eobAtClaimStart - prior.eob, 0),
        // The flat-Face residual DB opens net of prior utilization, floored —
        // a partially used policy opens reduced.
        deathBenefit: Math.max(faceAmount - utilizedToDate, minGuaranteed),
      },
      waitingPeriod,
      strategyState: isJoint
        ? { indemnitySurplus: 0, jointWaitingPeriod: seedJointWaitingPeriod(waitingPeriod) }
        : { indemnitySurplus: 0 },
    };
  },

  buildClaimTerms(client, ltcPolicy, jointAnchorPolicyYear): ClaimTerms {
    const { baseRate, eobRate, capYears, isSimple } = readInflation(ltcPolicy);
    const clientAsClient = client as Client;
    const projectionAnchorYear = getCareCostAnchorYear(clientAsClient);
    const { baseAtIssue, eobAtIssue } = readAtIssuePools(ltcPolicy);
    const isIndemnity = ltcPolicy.isIndemnityPolicyPayment === true;
    // The nominal base daily is the withdrawal-rate daily on the at-issue face
    // (policy year 1), so the displayed nominal matches the payout math rather
    // than a daily-benefit-derived figure these products do not quote.
    const baseDailyAtIssue = baseDailyCapForYear(ltcPolicy, 1);
    // Prior utilization drawn base (Acceleration) first, spilling into the COB
    // pool once the base is exhausted (mirrors `initClaimState`). Each leg is
    // capped at its pre-inflated pool at claim start, inflated to the SAME
    // anchor `initClaimState` used (the shared joint anchor when the partner
    // claims first) so the caps match the deduction.
    const claimStartPolicyYear =
      jointAnchorPolicyYear ?? getCalendarPolicyYear(clientAsClient, 0);
    const baseAtClaimStart =
      baseAtIssue * growthFactor(baseRate, capYears, isSimple, claimStartPolicyYear);
    const eobAtClaimStart = inflatedEobPoolForYear(ltcPolicy, claimStartPolicyYear);
    const prior = splitPriorUtilization(
      ltcPolicy.policyBenefitUtilizedToDate ?? 0,
      baseAtClaimStart,
      eobAtClaimStart,
      isUnlimitedCob(ltcPolicy),
    );
    const priorUtilization = prior.base + prior.eob;
    return {
      strategyId: 'hybrid-life-ira',
      priorUtilization,
      nominalDailyBenefit: baseDailyAtIssue,
      nominalDailyBenefitBase: baseDailyAtIssue,
      // COB rider daily by environment at policy year 1. Quoted dailies pass
      // through verbatim; products with no daily at all show the
      // withdrawal-rate fallback so the display matches the payout — see
      // header.
      nominalDailyBenefitByEnvironment: {
        [CareEnvironment.home]: cobDailyCapForYear(ltcPolicy, CareEnvironment.home, 1),
        [CareEnvironment.assistedLiving]: cobDailyCapForYear(
          ltcPolicy,
          CareEnvironment.assistedLiving,
          1,
        ),
        [CareEnvironment.fullCareFacility]: cobDailyCapForYear(
          ltcPolicy,
          CareEnvironment.fullCareFacility,
          1,
        ),
        [CareEnvironment.independentLiving]: cobDailyCapForYear(
          ltcPolicy,
          CareEnvironment.independentLiving,
          1,
        ),
      },
      inflationRate: baseRate,
      inflationCapYears: capYears,
      // Simple riders grow linearly (P2-38); surfaced so UI growth tooltips
      // narrate the right form.
      inflationIsSimple: isSimple,
      baseBenefitPoolInflationRate: baseRate,
      baseBenefitPoolInflationCapYears: capYears,
      extensionOfBenefitsPoolInflationRate: eobRate,
      extensionOfBenefitsPoolInflationCapYears: capYears,
      baseBenefitPoolAtIssue: baseAtIssue,
      extensionOfBenefitsPoolAtIssue: eobAtIssue,
      careCostInflationRate: appliedHealthCareInflation(clientAsClient),
      projectionAnchorYear,
      phaseStartYears: { ...(clientAsClient.clientPhasePredictedStartYears ?? {}) } as Record<
        CarePhase,
        number | null
      >,
      phaseCareMonthsByPhase: getPhaseCareMonthsByPhase(clientAsClient),
      firstPhaseStartYear: getFirstContributingPhaseStartYear(clientAsClient),
      policyPremiumStartYear: ltcPolicy.policyPremiumStartYear ?? projectionAnchorYear,
      minimumGuaranteedDeathBenefit: ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0,
      isReimbursement: !isIndemnity,
      isIndemnity,
      // Unlimited COB makes the LIFETIME pool unlimited (base + sentinel COB
      // never exhausts). UI surfaces gate on this to render "Unlimited"
      // instead of the raw ~$1B running balance; the base pool itself stays
      // finite, so per-pool surfaces must check each pool against the
      // sentinel rather than this flag.
      isUnlimited: isUnlimitedCob(ltcPolicy),
    };
  },

  buildYearSnapshot(input: BuildYearSnapshotInput): YearSnapshot {
    const { ltcPolicy, policyYear } = input;
    const { baseRate, capYears, isSimple } = readInflation(ltcPolicy);

    const baseFactor = growthFactor(baseRate, capYears, isSimple, policyYear);

    const { baseAtIssue } = readAtIssuePools(ltcPolicy);
    const inflatedAcceleration = baseAtIssue * baseFactor;
    // Flat at the sentinel when COB is unlimited, so the derived totals stay
    // >= the sentinel and render as "Unlimited".
    const inflatedEob = inflatedEobPoolForYear(ltcPolicy, policyYear);
    const faceAmount = ltcPolicy.policyMaximumBenefitAmount ?? 0;

    // Base monthly cap is the withdrawal-rate cap: inflatedFace × withdrawalRate
    // (= baseDailyCap × 30). COB monthly cap is the inflated per-environment
    // daily × 30 when a daily is quoted; with no quoted daily it falls back to
    // the withdrawal-rate cap (see cobDailyCapForYear). We use home as the
    // representative environment for the snapshot's COB daily.
    const baseDailyCap = baseDailyCapForYear(ltcPolicy, policyYear);
    const eobDailyCap = cobDailyCapForYear(ltcPolicy, CareEnvironment.home, policyYear);
    const monthlyBenefitCap = baseDailyCap * DAYS_IN_MONTH;
    const continuationMonthlyBenefitCap = eobDailyCap * DAYS_IN_MONTH;

    return {
      policyYear,
      effectiveDailyBenefit: baseDailyCap,
      effectiveDailyBenefitBase: baseDailyCap,
      effectiveDailyBenefitRider: eobDailyCap,
      effectiveFaceAmount: faceAmount,
      effectiveContinuationBenefit: inflatedEob,
      effectiveTotalLtcBenefit: inflatedAcceleration + inflatedEob,
      monthlyBenefitCap,
      continuationMonthlyBenefitCap,
      theoreticalLtcPoolCeiling: inflatedAcceleration + inflatedEob,
    };
  },

  computeMonth(
    input: MonthComputeInput<HybridLifeIraState, HybridLifeIraDeltas>,
  ): MonthlyEntry<HybridLifeIraState, HybridLifeIraDeltas> {
    const {
      client,
      ltcPolicy,
      phaseCarePlan,
      monthlyCareInput,
      openState,
      priorMonth,
      priorClaimantMonth,
    } = input;
    const { monthIndex, carePhase, monthsIntoPhase, claimant } = monthlyCareInput;
    const clientAsClient = client as Client;
    const jointWaitingPeriodBefore = openState.strategyState.jointWaitingPeriod;
    const isJoint = jointWaitingPeriodBefore != null;
    const activeClaimant: Claimant = claimant ?? 'client';

    const costMetrics = computeMonthlyCostMetrics(clientAsClient, phaseCarePlan, monthsIntoPhase);
    const claimYearOffset = Math.floor(monthIndex / 12);
    const policyYear = resolveRowPolicyYear(clientAsClient, monthIndex, input.jointAnchorPolicyYear);

    const activeWaitingPeriodBefore: WaitingPeriodState = jointWaitingPeriodBefore
      ? jointWaitingPeriodBefore[activeClaimant]
      : openState.waitingPeriod;
    const rowOpenState: MonthlyState<HybridLifeIraState> = isJoint
      ? { ...openState, waitingPeriod: activeWaitingPeriodBefore }
      : openState;

    const context: MonthContext = {
      monthIndex,
      policyYear,
      clientAge: calculateClientAge(clientAsClient.intakeSurvey?.clientAge, policyYear),
      claimYearOffset,
      carePhase,
      monthsIntoPhase,
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      claimant: monthlyCareInput.claimant,
    };

    // Pre-policy gate: a policy cannot pay for care before its premium-start
    // year (policy year < 1). Book full cost out-of-pocket, carry balances.
    if (policyYear < 1) {
      const prePolicyFacts: MonthlyCareFacts = {
        nominalCareCost: costMetrics.nominalCareCost,
        effectiveCareCost: costMetrics.effectiveCareCost,
        dailyCareCost: costMetrics.dailyCareCost,
        careCostInflationYears: costMetrics.careCostInflationYears,
        projectedCareYear: costMetrics.projectedCareYear,
        non2AdlDays: 0,
        coverageEligibleDays: 0,
        waitingPeriodDaysConsumedHome: 0,
        waitingPeriodDaysConsumedFacility: 0,
        appliedMonthlyBenefitCap: 0,
        appliedCapPoolSource: 'base',
      };
      return buildPrePolicyMonth<HybridLifeIraState, HybridLifeIraDeltas>({
        context,
        facts: prePolicyFacts,
        openState: rowOpenState,
        strategyDeltas: { indemnitySurplusAccrued: 0, indemnitySurplusApplied: 0 },
      });
    }

    // 1. Day classification (2-ADL bucket → WP → eligible). Identical machinery
    //    to Asset Care.
    const non2AdlDays = computeNon2AdlDaysThisRow(clientAsClient, phaseCarePlan, monthsIntoPhase);
    const daysAfter2Adl = DAYS_IN_MONTH - non2AdlDays;
    const wpConsumption = consumeWaitingPeriodDaysForEnvironment(
      activeWaitingPeriodBefore,
      phaseCarePlan.appliedCareEnvironment,
      daysAfter2Adl,
    );
    const {
      wpDaysThisRow,
      closeHomeWp,
      closeFacilityWp,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      closeRelevantWp,
    } = wpConsumption;
    const eligibleDays = daysAfter2Adl - wpDaysThisRow;

    const { baseRate, eobRate, capYears, isSimple } = readInflation(ltcPolicy);

    // Per-pool monthly caps for this year. Base cap = withdrawal-rate cap
    // (inflatedFace × withdrawalRate), zeroed for shop-insurance reimbursement
    // rows in Independent Living (P2-39 — legacy zeroes the daily once,
    // upstream of BOTH legs). COB cap = inflated per-environment daily
    // benefit × 30 when a daily is quoted; otherwise the withdrawal-rate cap
    // (see cobDailyCapForYear).
    const baseDailyCap = baseDailyCapForYear(
      ltcPolicy,
      policyYear,
      phaseCarePlan.appliedCareEnvironment,
    );
    const eobDailyCap = cobDailyCapForYear(
      ltcPolicy,
      phaseCarePlan.appliedCareEnvironment,
      policyYear,
    );
    const baseMonthlyCap = baseDailyCap * DAYS_IN_MONTH;
    const eobMonthlyCap = eobDailyCap * DAYS_IN_MONTH;

    // 2. Explicit-0 daily hard-zero (legacy `:618`): an explicitly-configured
    //    0 daily benefit for the active environment zeroes the phase. A MISSING
    //    daily benefit does NOT (legacy `:641` excludes hybridLifeIra) — the
    //    withdrawal-rate base path still fires.
    const explicitZeroDaily =
      rawDailyForEnvironment(ltcPolicy, phaseCarePlan.appliedCareEnvironment) === 0;

    // 3. Annual anniversary accrual against the AT-ISSUE amount, gated on
    //    "pool still > 0". Uses the global priorMonth so it fires once per
    //    shared policy-year boundary. An unlimited COB pool never accrues —
    //    it stays flat at the sentinel.
    let baseInflationAccrual = 0;
    let eobInflationAccrual = 0;
    if (priorMonth && policyYear !== priorMonth.context.policyYear) {
      const priorYear = priorMonth.context.policyYear;
      const { baseAtIssue, eobAtIssue } = readAtIssuePools(ltcPolicy);
      const baseFactorDelta =
        growthFactor(baseRate, capYears, isSimple, policyYear) -
        growthFactor(baseRate, capYears, isSimple, priorYear);
      const eobFactorDelta =
        growthFactor(eobRate, capYears, isSimple, policyYear) -
        growthFactor(eobRate, capYears, isSimple, priorYear);
      if (openState.pools.baseBenefitPool > 0) {
        baseInflationAccrual = baseAtIssue * baseFactorDelta;
      }
      if (openState.pools.extensionOfBenefitsPool > 0 && !isUnlimitedCob(ltcPolicy)) {
        eobInflationAccrual = eobAtIssue * eobFactorDelta;
      }
    }
    const baseAfterAccrual = openState.pools.baseBenefitPool + baseInflationAccrual;
    const eobAfterAccrual = openState.pools.extensionOfBenefitsPool + eobInflationAccrual;

    // 4. Payout — withdrawal-rate base, then COB rider. Base drawn first.
    //    Indemnity keeps both daily rates uncapped (NO indemnity-percent
    //    multiplier — legacy `:775-777`, `:791-793`); reimbursement caps each
    //    daily rate at the actual daily cost.
    const isIndemnity = ltcPolicy.isIndemnityPolicyPayment === true;
    const dailyCost = costMetrics.dailyCareCost;

    let drawFromBase = 0;
    let drawFromEob = 0;
    if (!explicitZeroDaily) {
      const basePerDay = isIndemnity ? baseDailyCap : Math.min(dailyCost, baseDailyCap);
      const baseDaysFunded =
        basePerDay > 0 ? Math.min(eligibleDays, baseAfterAccrual / basePerDay) : 0;
      drawFromBase = basePerDay * baseDaysFunded;

      const eobPerDay = isIndemnity ? eobDailyCap : Math.min(dailyCost, eobDailyCap);
      const eobDaysFunded =
        eobPerDay > 0 ? Math.min(eligibleDays - baseDaysFunded, eobAfterAccrual / eobPerDay) : 0;
      drawFromEob = eobPerDay * eobDaysFunded;
    }
    // Gross cash this row pays out across both pools. On indemnity this is the
    // uncapped daily-rate cash regardless of cost; on reimbursement it is the
    // reimbursed amount (≤ eligible cost). The pools deplete by this gross
    // cash; the surplus model is a separate accounting reserve on top.
    const rowPayout = drawFromBase + drawFromEob;
    const indemnityPaid = isIndemnity ? rowPayout : 0;

    // 4b. Indemnity-surplus accounting (per-row). Cost-coverage is capped at
    //     eligible cost; cash above eligible cost banks as surplus and drains
    //     on later shortfall rows. On reimbursement-election policies the open
    //     surplus is always 0 and the reimbursed payout never exceeds eligible
    //     cost, so this is a numeric no-op (no accrual, no drain).
    const eligibleCost = costMetrics.dailyCareCost * eligibleDays;
    const surplusResult = applyIndemnitySurplus({
      openSurplus: openState.strategyState.indemnitySurplus,
      indemnityPayout: rowPayout,
      eligibleCost,
      fullCost: costMetrics.effectiveCareCost,
      // Two pools (base + COB rider): exhausted only when BOTH are spent. The
      // 1e-6 epsilon absorbs sub-cent IEEE-754 dust from day-funding draws (plain
      // subtraction, not an exact `min`), matching cmTwo's threshold.
      policyExhausted: baseAfterAccrual + eobAfterAccrual <= 1e-6,
    });

    // 5. Closing balances. Only base drawdown depletes the death benefit (COB
    //    drawdown does not accelerate the face), same as Asset Care.
    const closeBase = baseAfterAccrual - drawFromBase;
    const closeEob = eobAfterAccrual - drawFromEob;
    const minGuaranteed = ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;
    const closeDeathBenefit = Math.max(openState.pools.deathBenefit - drawFromBase, minGuaranteed);
    const deathBenefitReduction = openState.pools.deathBenefit - closeDeathBenefit;
    const deathBenefitFloored =
      closeDeathBenefit === minGuaranteed && drawFromBase > deathBenefitReduction;

    const inCobPhase = closeBase <= 0 && (closeEob > 0 || drawFromEob > 0);

    const facts: MonthlyCareFacts = {
      nominalCareCost: costMetrics.nominalCareCost,
      effectiveCareCost: costMetrics.effectiveCareCost,
      dailyCareCost: costMetrics.dailyCareCost,
      careCostInflationYears: costMetrics.careCostInflationYears,
      projectedCareYear: costMetrics.projectedCareYear,
      non2AdlDays,
      coverageEligibleDays: eligibleDays,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      appliedMonthlyBenefitCap: inCobPhase ? eobMonthlyCap : baseMonthlyCap,
      appliedCapPoolSource: inCobPhase ? 'eob' : 'base',
    };

    const payout: PayoutSummary = {
      ltcCoveragePaid: surplusResult.ltcCoveragePaid,
      ltcPayoutToCost: surplusResult.ltcPayoutToCost,
      ineligibleCost: costMetrics.effectiveCareCost - surplusResult.ltcPayoutToCost,
      indemnityPayout: indemnityPaid,
    };

    const deltas: MonthlyDeltas<HybridLifeIraDeltas> = {
      payout,
      base: { depletion: drawFromBase, inflationAccrual: baseInflationAccrual },
      eob: { depletion: drawFromEob, inflationAccrual: eobInflationAccrual },
      deathBenefit: {
        reduction: deathBenefitReduction,
        flooredAtMinimum: deathBenefitFloored,
      },
      strategyDeltas: {
        indemnitySurplusAccrued: surplusResult.surplusAccrued,
        indemnitySurplusApplied: surplusResult.surplusApplied,
      },
    };

    const activeWaitingPeriodAfter: WaitingPeriodState = {
      homeDays: closeHomeWp,
      facilityDays: closeFacilityWp,
    };
    const closeState: MonthlyState<HybridLifeIraState> = {
      pools: {
        baseBenefitPool: closeBase,
        extensionOfBenefitsPool: closeEob,
        deathBenefit: closeDeathBenefit,
      },
      waitingPeriod: activeWaitingPeriodAfter,
      // The indemnity surplus is shared across the policy; carry the other
      // spouse's EP balance forward unchanged and update only the active one.
      strategyState: jointWaitingPeriodBefore
        ? {
            indemnitySurplus: surplusResult.closeSurplus,
            jointWaitingPeriod: {
              ...jointWaitingPeriodBefore,
              [activeClaimant]: activeWaitingPeriodAfter,
            },
          }
        : { indemnitySurplus: surplusResult.closeSurplus },
    };

    const status: MonthlyStatus = {
      ltcPoolExhausted: closeBase + closeEob <= 0,
      clientPoolExhausted: closeBase + closeEob <= 0,
      epSatisfied: closeRelevantWp === 0,
    };

    const milestones = deriveStandardMilestones<HybridLifeIraState, HybridLifeIraDeltas>({
      context,
      facts,
      openState: rowOpenState,
      closeState,
      deltas,
      priorMonth: priorClaimantMonth,
      retroactiveLumpPaid: 0,
      minimumGuaranteedDeathBenefit: minGuaranteed,
      clientHasStartedLtc: clientAsClient.intakeSurvey?.clientHasStartedLtc ?? false,
      hasInflationProtection: baseRate > 0 || eobRate > 0,
    });

    const ineligibilityInput = {
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      facts,
      openState: rowOpenState,
      closeState,
      payout,
    };
    const ineligibilityReason = deriveIneligibilityReason<HybridLifeIraState>(ineligibilityInput);
    const ineligibleDayBreakdown =
      deriveIneligibleDayBreakdown<HybridLifeIraState>(ineligibilityInput);

    return {
      context,
      facts,
      openState: rowOpenState,
      state: closeState,
      deltas,
      events: { status, milestones, ineligibilityReason, ineligibleDayBreakdown },
    };
  },

  finalizeDeathBenefit({ closingState }: FinalizeDeathBenefitInput<HybridLifeIraState>): number {
    return closingState.pools.deathBenefit;
  },
};
